import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CardBodyComponent,
  CardComponent,
  GroundworksTableComponent,
  GroundworksTableData,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  StateMessageComponent,
  TermValueComponent,
  TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ChangeOrderHistoryItem } from '../../sdk/sdk';
import { ChangeOrderService } from '../../services/change-order.service';
import { PaginatedModalHeaderComponent } from '../paginated-modal-header/paginated-modal-header.component';

@Component({
  selector: 'app-change-order-history-modal',
  templateUrl: './change-order-history-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardBodyComponent,
    CardComponent,
    GroundworksTableComponent,
    ModalBodyComponent,
    ModalComponent,
    ModalFooterComponent,
    StateMessageComponent,
    TermValueComponent,
    PaginatedModalHeaderComponent,
  ],
  styles: `
    :host ::ng-deep .modal-body {
      min-height: 250px;
    }
  `,
})
export class ChangeOrderHistoryModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly changeOrderService = inject(ChangeOrderService);
  private readonly destroyRef = inject(DestroyRef);

  readonly jobId = signal('');
  readonly history = signal<ChangeOrderHistoryItem[]>([]);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly currentIndex = signal(0);

  readonly currentEntry = computed<ChangeOrderHistoryItem | undefined>(
    () => this.history()[this.currentIndex()],
  );
  readonly totalEntries = computed(() => this.history().length);
  readonly isFirst = computed(() => this.currentIndex() === 0);
  readonly isLast = computed(() => this.currentIndex() === this.totalEntries() - 1);
  readonly positionLabel = computed(() => `${this.currentIndex() + 1}/${this.totalEntries()}`);
  readonly modalTitle = computed(() =>
    this.currentEntry() ? `Change Order History ${this.positionLabel()}` : 'Change Order History',
  );

  readonly summaryItems = computed<TermValueItem[]>(() => {
    const entry = this.currentEntry();
    if (!entry) return [];
    return [
      {
        term: 'Change Order Date',
        value: entry.changeOrderDate ?? '—',
        type: 'date' as const,
        format: 'MM/dd/yyyy',
      },
      {
        term: 'Created By',
        value: entry.createdBy ?? '—',
      },
      {
        term: 'Sold By',
        value: entry.soldByName ?? '—',
      },
      {
        term: 'Previous Job Total',
        value: entry.previousJobTotal ?? 0,
        type: 'currency' as const,
        variant: 'subtotal' as const,
      },
      {
        term: 'Net Change Amount',
        value: entry.netChangeAmount ?? 0,
        type: 'currency' as const,
      },
      {
        term: 'Change Order Total',
        value: entry.changeOrderTotal ?? 0,
        type: 'currency' as const,
        variant: 'total' as const,
      },
    ];
  });

  readonly upsellTableData = computed<GroundworksTableData>(() => ({
    columns: [
      { label: 'Product' },
      { label: 'Units', align: 'center' as const },
      { label: 'Salesperson', align: 'end' as const },
    ],
    rows: (this.currentEntry()?.upsellItems ?? []).map((item) => ({
      type: 'data' as const,
      cells: [
        { value: item.productName ?? '' },
        { value: `${item.quantity} ${item.unitOfMeasure ?? ''}` },
        { value: item.salespersonName ?? '—' },
      ],
    })),
  }));

  readonly descopeTableData = computed<GroundworksTableData>(() => ({
    columns: [
      { label: 'Product' },
      { label: 'Units', align: 'center' as const },
      { label: 'Salesperson', align: 'end' as const },
    ],
    rows: (this.currentEntry()?.descopeItems ?? []).map((item) => ({
      type: 'data' as const,
      cells: [
        { value: item.productName ?? '' },
        { value: `${item.quantity} ${item.unitOfMeasure ?? ''}` },
        { value: item.salespersonName ?? '—' },
      ],
    })),
  }));

  readonly hasUpsellItems = computed(() => (this.currentEntry()?.upsellItems?.length ?? 0) > 0);
  readonly hasDescopeItems = computed(() => (this.currentEntry()?.descopeItems?.length ?? 0) > 0);
  readonly hasTaxItems = computed(() => (this.currentEntry()?.taxItems?.length ?? 0) > 0);

  readonly taxesTableData = computed<GroundworksTableData>(() => ({
    columns: [
      { label: 'Tax' },
      { label: 'Previous', dataType: 'currency' as const },
      { label: 'New', dataType: 'currency' as const },
    ],
    rows: (this.currentEntry()?.taxItems ?? []).map((line) => ({
      type: 'data' as const,
      cells: [
        { value: line.taxTypeName ?? line.taxTypeId },
        { value: line.previousAmount },
        { value: line.newAmount },
      ],
    })),
  }));

  readonly retryButton = { text: 'Try Again', onClick: () => this.loadHistory() };

  readonly footerActions: ModalFooterAction[] = [
    {
      label: 'Close',
      type: 'outline-secondary',
      action: () => this.closeModal(),
    },
  ];

  constructor() {
    effect(() => {
      const id = this.jobId();
      if (id) {
        this.loadHistory();
      }
    });
  }

  previous(): void {
    if (!this.isFirst()) {
      this.currentIndex.update((i) => i - 1);
    }
  }

  next(): void {
    if (!this.isLast()) {
      this.currentIndex.update((i) => i + 1);
    }
  }

  closeModal(): void {
    this.activeModal.close();
  }

  private loadHistory(): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.currentIndex.set(0);

    this.changeOrderService
      .getHistory(this.jobId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          this.history.set(items);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.hasError.set(true);
        },
      });
  }
}
